import { NONE_SELECTED } from '@Components/ColumnFilterPopover';
import { RawComponentRow } from '../data-qa-types';

// Categorical columns the Raw Components table lets the reviewer narrow by, via
// ColumnFilterPopover -- same facet pattern as Campaigns' SourceGroupTable. Campaign Name/
// Campaign ID are filtered separately by free-text search instead (effectively unique per
// row, so a checkbox pick-list wouldn't help).
export type FilterableColumn = 'source' | 'channel' | 'platform' | 'tactic';

export const FILTERABLE_COLUMNS: FilterableColumn[] = ['source', 'channel', 'platform', 'tactic'];

export type FacetSelection = Record<FilterableColumn, ReadonlySet<string>>;

export const emptyFacetSelection = (): FacetSelection => ({
  source: new Set(),
  channel: new Set(),
  platform: new Set(),
  tactic: new Set()
});

// A row's value in a filterable column. `source` is absent from a load response built
// before the backend started sending it, which reads as ''.
export const columnValue = (row: RawComponentRow, column: FilterableColumn): string =>
  row[column] ?? '';

// A column with nothing selected passes every row (no filter applied yet). NONE_SELECTED
// means the opposite extreme -- explicitly excludes every row. Otherwise a row must match
// one of the column's selected values, while columns are ANDed together -- same semantics
// as campaigns-filter-utils' facetPasses.
const facetPasses = (
  row: RawComponentRow,
  column: FilterableColumn,
  selected: ReadonlySet<string>
): boolean => {
  if (selected.has(NONE_SELECTED)) return false;
  if (selected.size === 0) return true;
  return selected.has(columnValue(row, column));
};

export const filterRowsByFacets = (
  rows: RawComponentRow[],
  selection: FacetSelection
): RawComponentRow[] =>
  rows.filter(row =>
    FILTERABLE_COLUMNS.every(column => facetPasses(row, column, selection[column]))
  );

// Options for one column's popover, with counts against this feature's full raw-component
// set (not re-scoped by the other columns' current selections) -- matches
// campaigns-filter-utils' distinctColumnValues.
export const distinctColumnValues = (
  rows: RawComponentRow[],
  column: FilterableColumn
): { value: string; count: number }[] => {
  const counts = new Map<string, number>();
  rows.forEach(row => {
    const value = columnValue(row, column);
    counts.set(value, (counts.get(value) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value.localeCompare(b.value));
};
